/**
 * Device utilities for MFA "once per day per device" feature
 * Generates and manages persistent device identification
 */

const DEVICE_ID_KEY = 'copima_device_id';

/**
 * Generate a UUID v4
 * @returns {string} UUID string
 */
const generateUUID = () => {
  // Use crypto.randomUUID if available (modern browsers)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback for older browsers (bitwise ops intentional for UUID generation)
  // eslint-disable-next-line no-bitwise
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0; // eslint-disable-line no-bitwise
    const v = c === 'x' ? r : (r & 0x3) | 0x8; // eslint-disable-line no-bitwise
    return v.toString(16);
  });
};

/**
 * Get or create a persistent device ID
 * This ID is stored in localStorage and persists across sessions
 * @returns {string} Device ID
 */
export const getDeviceId = () => {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);

  if (!deviceId) {
    deviceId = generateUUID();
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }

  return deviceId;
};

/**
 * Get the current timezone
 * @returns {string} Timezone string (e.g., "Africa/Casablanca")
 */
export const getTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    return null;
  }
};

/**
 * Get the screen resolution
 * @returns {string} Screen resolution (e.g., "1920x1080")
 */
export const getScreenResolution = () => {
  try {
    return `${window.screen.width}x${window.screen.height}`;
  } catch (e) {
    return null;
  }
};

/**
 * Get the browser language
 * @returns {string} Language code (e.g., "fr-FR")
 */
export const getLanguage = () => {
  try {
    return navigator.language || navigator.userLanguage;
  } catch (e) {
    return null;
  }
};

/**
 * Get all device info for MFA requests
 * @returns {Object} Object containing deviceId, timezone, screenResolution, language
 */
export const getDeviceInfo = () => ({
  deviceId: getDeviceId(),
  timezone: getTimezone(),
  screenResolution: getScreenResolution(),
  language: getLanguage(),
});

/**
 * Clear device ID (useful for testing or if user wants to "forget" device)
 */
export const clearDeviceId = () => {
  localStorage.removeItem(DEVICE_ID_KEY);
};

export default {
  getDeviceId,
  getDeviceInfo,
  getTimezone,
  getScreenResolution,
  getLanguage,
  clearDeviceId,
};
